/**
 * mediaAssetUrl — the shared img-src coercion rule for CMS draft img-src fields.
 *
 * `coerceHeroImageUrl` is the single source of truth for "is this raw img-src
 * value an accepted media asset, and if so what is its normalised form?". It
 * mirrors the server's `mediaAssetUrlSchema` union (same-origin asset path OR
 * `https://` URL) and is the rule the publish-side validators apply.
 *
 * ## Why this module exists (boundary)
 *
 * The rule was authored inline in `TemplateForms/serviceClientSchema.ts` (the
 * article form validator). Multiple consumers now need it:
 *
 *   - `serviceClientSchema.ts` (re-exports + uses it for `heroImage-https`),
 *   - `benefitClientSchema.ts` / `videoListingClientSchema.ts` (logo / thumbnail
 *     coercion),
 *   - `PreviewPane.tsx` (hardens a draft's img-src at its injection seam),
 *   - `preview-frame/PreviewOverlayContentReader.ts` (hardens a draft's img-src
 *     before it reaches a member component in the iframe preview).
 *
 * The iframe-preview boundary (`preview-frame/`) reaching UP into the admin
 * forms subtree (`TemplateForms/`) for this rule was an undeclared upward
 * dependency. Hoisting the rule to a dedicated CMS-level module gives every
 * consumer a peer import and keeps `serviceClientSchema` re-exporting it, so no
 * existing import site changes and the rule has exactly one definition (P7
 * no-shadow-copy).
 *
 * Pure and side-effect-free: no React, no vendor SDK, no module-level state.
 */

const HTTPS_PREFIX = 'https://';

/**
 * Same-origin asset path matcher — mirrors the server-side
 * `sameOriginAssetPathSchema` regex. Accepts `/foo/bar.png`, rejects
 * `//evil.example.com/foo` (protocol-relative URL), `/` (bare slash),
 * `media/foo.png` (no leading slash).
 */
const SAME_ORIGIN_ASSET_PATH = /^\/[^/].*$/u;

/**
 * Coerce a raw hero/media-image string to the accepted URL it represents, or
 * `null` when the value is not an accepted media asset.
 *
 * Acceptance rule (mirrors the server's `mediaAssetUrlSchema` union):
 *
 *   - empty / whitespace-only           → `null`
 *   - same-origin asset path (`/x.png`) → the trimmed value (the
 *     `SAME_ORIGIN_ASSET_PATH` regex rejects protocol-relative `//host/x`,
 *     bare slash `/`, and bare filenames)
 *   - `https://…` that parses via `new URL()` → the trimmed value
 *   - anything else (`http:`, `javascript:`, `data:`, unparseable) → `null`
 *
 * Pure and side-effect-free.
 */
export function coerceHeroImageUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return null;
  }
  if (SAME_ORIGIN_ASSET_PATH.test(trimmed)) {
    // Same-origin path branch — `/media/x.png`, `/images/foo.jpg`. The regex
    // rejects `//evil.example.com/x.png` (protocol-relative), bare slash `/`,
    // and bare filename (no leading slash).
    return trimmed;
  }
  if (trimmed.startsWith(HTTPS_PREFIX)) {
    try {
      // Tracks the server's `z.string().url()` baseline. WHATWG URL
      // constructor + the prefix guard above together approximate
      // `safeUrlSchema` minus the explicit allowlist refine.
      new URL(trimmed);
      return trimmed;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * `isSameOriginAssetPath` — true when `raw` (trimmed) is a same-origin asset
 * path (`/media/x.mp4`, `/images/foo.png`); false otherwise.
 *
 * Shares the single `SAME_ORIGIN_ASSET_PATH` regex with `coerceHeroImageUrl`
 * (P7 no-shadow-copy — the same-origin-path rule has exactly ONE definition).
 * Like the coercer's same-origin branch it rejects protocol-relative `//host/x`,
 * the bare slash `/`, and bare filenames (no leading slash).
 *
 * The boundary predicate the media re-point needs: it tells a CMS-hosted asset
 * (legitimately on this origin) apart from an external destination URL. Used by
 * the dual-mode `videoUrl` rule (`coerceVideoUrl`) and the member-render
 * self-hosted-vs-external classification (`LakiruutuVideoCard`).
 *
 * Pure and side-effect-free.
 */
export function isSameOriginAssetPath(raw: string): boolean {
  return SAME_ORIGIN_ASSET_PATH.test(raw.trim());
}
